import moment from "moment";
import { paymentMethods, pgMethods } from "../../../reusable/common";

export const eventStatusOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active Events" },
    { value: "past", label: "Past Events" },
];

export const dateFilterOptions = [
    { value: "lastWeek", label: "Last Week" },
    { value: "lastMonth", label: "Last Month" },
    { value: "last3Months", label: "Last 3 Months" },
    { value: "custom", label: "Custom Range" },
];

export const viewTypeOptions = [
    { value: "card", label: "Card View" },
    { value: "table", label: "Table View" },
];

export const getDateRangeISO = (filter, customRange = null) => {
    const now = new Date();
    let fromDate, toDate;

    // Handle custom date range first
    if (filter === 'custom' && customRange && customRange?.startDate && customRange?.endDate) {
        fromDate = moment(customRange?.startDate)?.startOf('day')?.toISOString();
        toDate = moment(customRange?.endDate)?.endOf('day')?.toISOString();
        return { from_timestamp: fromDate, to_timestamp: toDate };
    }

    // Default end date is now
    toDate = now.toISOString();

    switch (filter) {
        case 'lastWeek':
            fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
            break;
        case 'lastMonth':
            fromDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).toISOString();
            break;
        case 'last3Months':
            fromDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()).toISOString();
            break;
        case 'lastYear':
            fromDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString();
            break;
        default:
            fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    }

    return { from_timestamp: fromDate, to_timestamp: toDate };
};


export const getWhichCountrySymbolForDollar = (currency) => {
    const countrySignMap = {
        "AUD": "A",   // Australia 
        "CAD": "CA",  // Canada
        "HKD": "HK",  // Hong Kong
        "NZD": "NZ",  // New Zealand
        "SGD": "S",   // Singapore
    };
    return countrySignMap[currency] || "";
};

export const formatEventDateTime = (date, time) => {
    if (!date || !time) return date || 'No date';
    const dateTimeString = `${date} ${time}`;
    return moment(dateTimeString, "YYYY-MM-DD HH:mm:ss").format("MMM D, YYYY h:mm A");
};

export const formatCurrency = (value, currencySymbol = "₹") => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (!numValue || numValue === 0 || isNaN(numValue)) {
        return `${currencySymbol}0`;
    }

    if (numValue >= 10000000) {
        return `${currencySymbol}${(numValue / 10000000).toFixed(1)}Cr`;
    } else if (numValue >= 100000) {
        return `${currencySymbol}${(numValue / 100000).toFixed(1)}L`;
    } else if (numValue >= 1000) {
        return `${currencySymbol}${(numValue / 1000).toFixed(1)}K`;
    }
    return `${currencySymbol}${numValue.toLocaleString()}`;
};
export const formatNumber = (value) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    if (!numValue || numValue === 0 || isNaN(numValue)) {
        return "0";
    }
    return numValue.toLocaleString();
};

export const transformPaymentStatsData = (apiResponse) => {
    if (!apiResponse?.reports) {
        return { paymentMethods: [], paymentGateways: [] };
    }

    const { payment_methods = {}, payment_gateways = {} } = apiResponse?.reports;

    const methodsData = Object.entries(payment_methods)?.map(([methodId, count]) => ({
        id: parseInt(methodId),
        name: paymentMethods(parseInt(methodId)) || `Method ${methodId}`,
        value: count,
        count: count
    }));

    const gatewaysData = Object.entries(payment_gateways).map(([gatewayId, count]) => ({
        id: parseInt(gatewayId),
        name: pgMethods[parseInt(gatewayId)] || `Gateway ${gatewayId}`,
        value: count,
        count: count
    }));

    const calculatePercentages = (data) => {
        const total = data?.reduce((sum, item) => sum + item?.value, 0);

        if (total === 0) return data;

        return data?.map(item => ({
            ...item,
            percentage: parseFloat(((item?.value / total) * 100)?.toFixed(2))
        }));
    };

    return {
        paymentMethods: calculatePercentages(methodsData),
        paymentGateways: calculatePercentages(gatewaysData)
    };
};
 export const chartColorScheme = ["#002E6E", "#2E88E9", "#95C1FF", "#A319C3", "#783AA8", "#f97316", "#10b981"];

export const TimezoneUtils = {
    getTimezoneData() {
        try {
            const resolved = Intl?.DateTimeFormat?.()?.resolvedOptions?.() || {};
            const now = new Date();

            return {
                // Timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
                timeZone: resolved?.timeZone || 'UTC',

                // Offset in minutes (positive for ahead of UTC, negative for behind)
                offsetMinutes: -(now?.getTimezoneOffset?.() || 0),

                // Formatted offset string (e.g., "+05:30", "-05:00")
                offsetString: this?.formatOffset?.(-(now?.getTimezoneOffset?.() || 0)) || '+00:00',

                // Locale information
                locale: resolved?.locale || 'en-US',

                // Check if DST is currently active
                isDST: this?.isDaylightSavingTime?.(now) || false
            };
        } catch (error) {
            return {
                timeZone: 'UTC',
                offsetMinutes: 0,
                offsetString: '+00:00',
                locale: 'en-US',
                isDST: false
            };
        }
    },

    // Format offset as string
    formatOffset(minutes) {
        try {
            const safeMinutes = minutes || 0;
            const sign = safeMinutes >= 0 ? '+' : '-';
            const absMinutes = Math?.abs?.(safeMinutes) || 0;
            const hours = Math?.floor?.(absMinutes / 60) || 0;
            const mins = absMinutes % 60;
            return `${sign}${hours?.toString?.()?.padStart?.(2, '0') || '00'}:${mins?.toString?.()?.padStart?.(2, '0') || '00'}`;
        } catch (error) {
            return '+00:00';
        }
    },

    // Check if current date is in DST
    isDaylightSavingTime(date) {
        try {
            if (!date || !date?.getFullYear || !date?.getTimezoneOffset) {
                return false;
            }

            const january = new Date(date?.getFullYear?.(), 0, 1)?.getTimezoneOffset?.() || 0;
            const july = new Date(date?.getFullYear?.(), 6, 1)?.getTimezoneOffset?.() || 0;
            const currentOffset = date?.getTimezoneOffset?.() || 0;

            return Math?.max?.(january, july) !== currentOffset;
        } catch (error) {
            return false;
        }
    },

    // Get offset for a specific date (handles DST changes)
    getOffsetForDate(date) {
        try {
            if (!date || !date?.getTimezoneOffset) {
                return 0;
            }
            return -(date?.getTimezoneOffset?.() || 0);
        } catch (error) {
            return 0;
        }
    }
};

export const EVENT_STATUS = {
  ACTIVE: 1,
  UPCOMING: 2,
  PAST: 3,
};

export const CURRENCY = {
  INR: "INR",
  USD: "USD",
  EUR: "EUR",
  // add others as needed
};